<template>
  <div class="appointment-entry-form">
    <b-form @submit.stop.prevent="formOnSubmit">
      <div class="row no-gutters align-items-center mx-n2">
        <!-- Input Modal Trigger -->
        <div class="col-12 px-2">
          <b-form-group
            id="input-group-trigger-modal-patient"
            label="Pasien:"
            label-for="input-trigger-modal-patient"
          >
            <button
              id="input-trigger-modal-patient"
              :disabled="purpose == 'edit' && form.appointment_status_id == 4"
              type="button"
              class="appointment-patient-picker"
              aria-haspopup="dialog"
              @click="$bvModal.show('modal-patient')"
            >
              <span class="appointment-patient-picker__icon" aria-hidden="true">
                <i class="far fa-user"></i>
              </span>
              <span
                class="appointment-patient-picker__value"
                :class="{ 'appointment-patient-picker__value--placeholder': !display.patient_name }"
              >
                {{ display.patient_name || 'Pilih Pasien' }}
              </span>
              <i class="fas fa-chevron-right appointment-patient-picker__arrow" aria-hidden="true"></i>
            </button>
            <small class="text-danger">{{ error.patient_id }}</small>
          </b-form-group>
        </div>

      </div>

      <div class="coverage-panel mb-3">
        <div
          class="coverage-panel__grid"
          :class="{ 'coverage-panel__grid--bpjs': form.coverage_type === 'bpjs' }"
        >
          <div class="coverage-panel__payment">
            <b-form-group label="Cara pembayaran kunjungan:">
              <b-form-radio-group
                v-model="form.coverage_type"
                :options="coverageOptions"
                name="coverage-type"
                class="coverage-radio-group"
              />
              <small class="text-danger">{{ error.coverage_type }}</small>
            </b-form-group>
          </div>

          <div v-if="form.coverage_type === 'bpjs'" class="coverage-panel__verification">
            <b-form-group label="NIK / nomor kartu JKN:">
              <b-input-group class="bpjs-lookup">
                <b-form-input
                  v-model.trim="bpjsLookupNumber"
                  inputmode="numeric"
                  maxlength="20"
                  placeholder="NIK otomatis dari data pasien"
                  @input="resetBpjsVerification"
                />
                <b-input-group-append>
                  <b-button
                    class="bpjs-check-button"
                    variant="success"
                    :disabled="bpjsChecking || !bpjsLookupValid"
                    @click="verifyBpjsParticipant"
                  >
                    <b-spinner v-if="bpjsChecking" small />
                    <template v-else>
                      <i class="fas fa-search mr-1"></i>Cek BPJS
                    </template>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <small class="text-danger">{{ error.bpjs_card_number }}</small>
            </b-form-group>

            <div v-if="form.bpjs_verified_at" class="bpjs-status bpjs-status--ok">
              <i class="fas fa-check-circle"></i>
              <span>
                Terverifikasi · {{ form.bpjs_participant_status || "status ditemukan" }}
                <template v-if="form.bpjs_fktp_name"> · {{ form.bpjs_fktp_name }}</template>
              </span>
            </div>
            <div v-else class="bpjs-status">
              <i class="fas fa-info-circle"></i>
              <span>Verifikasi kepesertaan wajib sebelum kunjungan disimpan.</span>
            </div>
          </div>
        </div>

        <div v-if="form.coverage_type === 'bpjs'" class="coverage-panel__complaint">
          <b-form-group label="Keluhan awal:">
            <b-form-input
              v-model.trim="form.complaint"
              maxlength="1000"
              placeholder="Keluhan untuk pendaftaran antrean/kunjungan PCare"
            />
          </b-form-group>
        </div>
      </div>

      <div class="row no-gutters mx-n2">
        <div class="col-lg-6 col-12 px-2">
          <b-form-group id="input-group-date" label="Tanggal:" label-for="input-date">
            <v-dialog
              v-model="dateMenu"
              transition="scale-transition"
              content-class="appointment-date-dialog"
              max-width="360"
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  id="input-date"
                  type="button"
                  class="appointment-date-trigger"
                  :disabled="purpose == 'edit' && form.appointment_status_id == 4"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span class="appointment-date-trigger__icon" aria-hidden="true">
                    <i class="far fa-calendar-alt"></i>
                  </span>
                  <span :class="{ 'is-placeholder': !form.date }">{{ appointmentDateLabel }}</span>
                  <i class="fas fa-chevron-down appointment-date-trigger__chevron"></i>
                </button>
              </template>
              <v-date-picker
                v-model="form.date"
                class="appointment-date-calendar"
                locale="id"
                :min="minimumAppointmentDate"
                color="#1E958F"
                no-title
                scrollable
                width="360"
                @input="dateMenu = false"
              />
            </v-dialog>
            <small class="text-danger">{{ error.date }}</small>
          </b-form-group>
        </div>

        <div class="col-lg-6 col-12 px-2">
          <b-form-group
            v-if="currentUser.user_type_id != 1"
            id="input-group-doctor"
            label="Dokter:"
            label-for="input-doctor"
          >
            <treeselect
              v-model="form.doctor_id"
              :multiple="false"
              :disabled="doctorSelectDisabled"
              :placeholder="doctorSelectPlaceholder"
              @open="handleDoctorOpen"
              @select="inputDoctorOnSelect"
              :options="doctors"
            />
            <small v-if="!form.date" class="appointment-field-hint">
              Pilih tanggal kunjungan terlebih dahulu.
            </small>
            <small
              v-else-if="!doctorsLoading && doctors.length === 0"
              class="appointment-field-hint appointment-field-hint--warning"
            >
              Tidak ada dokter dengan sesi praktik tersisa pada tanggal ini.
            </small>
            <small class="text-danger">{{ error.doctor_id }}</small>
          </b-form-group>
        </div>
      </div>

      <b-form-group v-if="form.doctor_id != ''">
        <template>
          <div>
            <div
              style="font-size: 1.1rem"
              v-html="selectedDoctorScheduleText"
            ></div>
            <button class="btn-link" @click.stop.prevent="createDoctorSchedule">
              Tambah Jadwal
            </button>
          </div>
        </template>
      </b-form-group>

      <div class="row no-gutters mx-n2">
        <!-- Input Action Types -->
        <div class="col-lg-6 col-12 px-2 order-2">
          <b-form-group id="input-group-action_type">
            <label for="input-action-type">
              Jenis Tindakan:
              <em class="text-muted">opsional</em>
            </label>
            <treeselect
              v-model="form.action_type_id"
              :multiple="false"
              :disabled="!form.doctor_id || actionTypesLoading"
              :placeholder="actionTypePlaceholder"
              :options="actionTypes"
            />
            <small v-if="!form.doctor_id" class="appointment-field-hint">
              Pilih dokter agar tindakan sesuai layanan dokter dan poli.
            </small>
            <small
              v-else-if="!actionTypesLoading && actionTypes.length === 0"
              class="appointment-field-hint appointment-field-hint--warning"
            >
              Dokter ini belum memiliki layanan tindakan. Atur melalui menu Komisi Dokter.
            </small>
            <small class="text-danger">{{ error.action_type_id }}</small>
          </b-form-group>
        </div>

        <!-- Input Time Slot -->
        <div class="col-lg-6 col-12 px-2 order-1">
          <b-form-group
            id="input-group-time-slot"
            label="Slot Waktu:"
            label-for="input-time-slot"
          >
            <b-form-select
              v-model="form.doctor_time_slot_id"
              :options="timeSlots"
              :disabled="purpose == 'edit' && form.appointment_status_id == 4"
              placeholder="Slot Waktu"
            ></b-form-select>
            <small class="text-danger">{{ error.doctor_time_slot_id }}</small>
          </b-form-group>
        </div>
      </div>

      <!-- Input Quote Time Slot -->
      <div
        class="row no-gutters justify-content-end mx-n2"
        v-if="form.doctor_time_slot_id == 'custom'"
      >
        <div class="col-lg-3 col-12 px-2">
          <!-- <b-form-group
            id="input-group-quote-start-time"
            label="Jam Mulai:"
            label-for="input-quote-start-time"
          >
            <b-form-timepicker
              v-model="form.quote_start_time"
              placeholder="Jam Mulai"
              label-now-button="Waktu Sekarang"
              locale="id"
              now-button
              reset-button
              minutes-step="5"
              @hidden="inputQuoteStartTimeOnHidden"
            ></b-form-timepicker>
            <small class="text-danger">{{ error.quote_start_time }}</small>
          </b-form-group> -->

          <label for="input-start-time"> Jam Mulai</label>
          <b-input-group>
            <b-input-group-prepend>
              <b-form-timepicker
                v-model="form.quote_start_time"
                locale="id"
                placeholder="Waktu"
                button-only
                now-button
                reset-button
                minutes-step="5"
              ></b-form-timepicker>
            </b-input-group-prepend>

            <b-form-input
              id="input-start-time"
              v-model="form.quote_start_time"
              type="text"
              placeholder="HH:MM:SS"
              @input="formaterTimeStart"
            ></b-form-input>
            <small class="text-danger">{{ error.quote_start_time }}</small>
          </b-input-group>
        </div>

        <div class="col-lg-3 col-12 px-2">
          <!-- <b-form-group
            id="input-group-quote-end-time"
            label="Jam Selesai:"
            label-for="input-quote-end-time"
          >
            <b-form-timepicker
              v-model="form.quote_end_time"
              locale="id"
              placeholder="Jam Selesai"
              now-button
              label-now-button="Waktu Sekarang"
              reset-button
              minutes-step="5"
              @hidden="inputQuoteEndTimeOnHidden"
            ></b-form-timepicker>
            <small class="text-danger">{{ error.quote_end_time }}</small>
          </b-form-group> -->

          <label for="input-end-time"> Jam Selesai</label>
          <b-input-group>
            <b-input-group-prepend>
              <b-form-timepicker
                v-model="form.quote_end_time"
                locale="id"
                placeholder="Waktu"
                button-only
                now-button
                reset-button
                minutes-step="5"
              ></b-form-timepicker>
            </b-input-group-prepend>

            <b-form-input
              id="input-end-time"
              v-model="form.quote_end_time"
              type="text"
              placeholder="HH:MM:SS"
              @input="formaterTimeEnd"
            ></b-form-input>
            <small class="text-danger">{{ error.quote_end_time }}</small>
          </b-input-group>
        </div>
      </div>

      <!-- Input Room -->
      <b-form-group id="input-group-room">
        <label for="input-room">
          Ruangan:
          <em class="text-muted">opsional</em>
        </label>
        <treeselect
          v-model="form.room_id"
          :multiple="false"
          :options="rooms"
          placeholder="Pilih Ruangan"
        />
        <small class="text-danger">{{ error.room_id }}</small>
      </b-form-group>

      <!-- Input Remarks / Note -->
      <b-form-group id="input-group-notes">
        <label for="input-notes">
          Catatan:
          <em class="text-muted">opsional</em>
        </label>
        <b-form-input
          id="input-notes"
          v-model="form.notes"
          placeholder="Catatan"
        ></b-form-input>
      </b-form-group>

      <!-- Input Live Meeting Link -->
      <b-form-group id="input-group-live-meeting-link">
        <label for="input-live-meeting-link">
          Tautan Meeting:
          <em class="text-muted">opsional</em>
        </label>
        <b-form-input
          id="input-live-meeting-link"
          v-model="form.live_meeting_link"
          placeholder="Tautan Meeting"
        ></b-form-input>
      </b-form-group>

      <!-- Input Status -->
      <b-form-group
        id="input-group-status"
        label="Status Perjanjian:"
        label-for="input-status"
      >
        <b-form-select
          id="input-status"
          v-model="form.appointment_status_id"
          :disabled="purpose == 'edit' && form.appointment_status_id == 4"
          :options="statuses"
        ></b-form-select>
        <small class="text-danger">{{ error.appointment_status_id }}</small>
      </b-form-group>

      <div v-if="formError" class="appointment-form-error" role="alert">
        <i class="fas fa-exclamation-circle"></i>
        <span>{{ formError }}</span>
      </div>

      <div class="appointment-entry-form__actions">
        <b-button type="submit" variant="primary">Simpan perjanjian</b-button>
        <b-button
          variant="default"
          @click="$router.push('/appointment/list/0')"
          >Batal</b-button
        >
      </div>
    </b-form>

    <ModalPatient
      :selectedDoctor="form.doctor_id.toString()"
      :patient-prefill="bpjsPatientPrefill"
      @chosenPatient="setPatient"
      @submitedPatient="setPatient"
      source="appointment"
    />
  </div>
</template>

<script>
import Card from "@/view/content/Card.vue";
import ModalPatient from "@/component/general/ModalPatient.vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import AppointmentModule from "@/core/modules/AppointmentModule.js";
import module from "@/core/modules/CrudModule.js";
import DoctorModule from "@/core/modules/DoctorModule";
import PaymentModule from "@/core/modules/PaymentModule";
import ApiService from "@/core/services/api.service";
import validation from "@/core/modules/ValidationModule.js";
import {
  getUser,
  setAppointmentForm,
  getAppointmentForm,
  removeAppointmentForm,
} from "@/core/services/jwt.service.js";

export default {
  props: {
    route: String,
    initialForm: {
      type: Object,
      required: true,
    },
    purpose: String,
  },

  components: {
    Card,
    ModalPatient,
    Treeselect,
  },

  data() {
    return {
      form: Object.assign(
        {},
        this.initialForm,
        getAppointmentForm() || {}
      ),
      display: { patient_name: "" },
      dateMenu: false,
      bpjsLookupNumber: "",
      bpjsChecking: false,
      bpjsPatientPrefill: {},
      doctorRequestId: 0,
      doctorsLoading: false,
      doctorLoadError: false,
      actionTypesLoading: false,
      roomRequestId: 0,
      actionTypeRequestId: 0,
      coverageOptions: [
        { text: "Umum", value: "umum" },
        { text: "BPJS/JKN", value: "bpjs" },
      ],
      error: {
        patient_id: "",
        doctor_id: "",
        date: "",
        registration_date: "",
        doctor_time_slot_id: "",
        action_type_id: "",
        room_id: "",
        quote_start_time: "",
        quote_end_time: "",
        appointment_status_id: "",
        coverage_type: "",
        bpjs_card_number: "",
        bpjs_verified_at: "",
        complaint: "",
      },
      formError: "",
      timeSlots: [{ text: "Pilih Jadwal", value: "", disabled: true }],
      actionTypes: [],
      rooms: [{ label: "Pilih Ruangan", id: "", isDisabled: true }],
      doctors: [{ label: "Pilih Dokter", id: "", isDisabled: true }],
      statuses: [
        { text: "Pilih Status Perjanjian", value: "", disabled: true },
      ],
      doctorDisabled: this.purpose == "edit" ? true : false,
      selectedDoctorSchedule: [],
      selectedDoctorScheduleText: "",
      currentUser: getUser(),
    };
  },

  computed: {
    bpjsLookupValid() {
      return /^\d{8,20}$/.test(this.bpjsLookupNumber);
    },
    minimumAppointmentDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    appointmentDateLabel() {
      if (!this.form.date) return "Pilih tanggal kunjungan";
      const date = new Date(`${this.form.date}T00:00:00`);
      if (Number.isNaN(date.getTime())) return this.form.date;
      return new Intl.DateTimeFormat("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(date);
    },
    doctorSelectDisabled() {
      return this.purpose == "edit" && this.form.appointment_status_id == 4;
    },
    doctorSelectPlaceholder() {
      if (!this.form.date) return "Pilih tanggal terlebih dahulu";
      if (this.doctorsLoading) return "Memuat dokter tersedia...";
      if (this.doctorLoadError) return "Gagal memuat dokter - klik untuk mencoba lagi";
      if (this.doctors.length === 0) return "Tidak ada dokter tersedia";
      return "Pilih dokter";
    },
    actionTypePlaceholder() {
      if (!this.form.doctor_id) return "Pilih dokter terlebih dahulu";
      if (this.actionTypesLoading) return "Memuat tindakan dokter...";
      if (this.actionTypes.length === 0) return "Belum ada tindakan untuk dokter ini";
      return "Pilih Jenis Tindakan";
    },
  },

  methods: {
    validateBeforeSubmit() {
      validation.clearValidationError(this.error);
      this.formError = "";
      const requiredFields = [
        ["date", "Tanggal kunjungan"],
        ["patient_id", "Pasien"],
        ["doctor_id", "Dokter"],
        ["doctor_time_slot_id", "Slot waktu"],
        ["appointment_status_id", "Status perjanjian"],
      ];

      if (!this.form.coverage_type) {
        requiredFields.push(["coverage_type", "Cara pembayaran"]);
      }
      if (this.form.coverage_type === "bpjs") {
        if (!this.form.bpjs_card_number) {
          requiredFields.push(["bpjs_card_number", "Nomor kartu BPJS"]);
        }
        if (!this.form.bpjs_verified_at) {
          requiredFields.push(["bpjs_verified_at", "Verifikasi kepesertaan BPJS"]);
        }
      }
      if (this.form.doctor_time_slot_id === "custom") {
        requiredFields.push(["quote_start_time", "Jam mulai"]);
        requiredFields.push(["quote_end_time", "Jam selesai"]);
      }

      const missing = requiredFields.filter(([key]) => {
        const value = this.form[key];
        return value === null || value === undefined || String(value).trim() === "";
      });
      missing.forEach(([key, label]) => {
        this.$set(this.error, key, `${label} tidak boleh kosong`);
      });

      if (this.form.date && this.form.date < this.minimumAppointmentDate) {
        this.$set(this.error, "date", "Tanggal kunjungan tidak boleh sudah lewat");
      }
      if (
        this.form.doctor_time_slot_id === "custom" &&
        this.form.quote_start_time &&
        this.form.quote_end_time &&
        this.form.quote_end_time <= this.form.quote_start_time
      ) {
        this.$set(this.error, "quote_end_time", "Jam selesai harus setelah jam mulai");
      }

      const firstError = Object.values(this.error).find(Boolean);
      if (firstError) {
        this.formError = firstError;
        return false;
      }
      return true;
    },
    formaterDate(val) {
      if (val.length == 4) {
        this.form.date = this.form.date + "-";
      }
      if (val.length == 7) {
        this.form.date = this.form.date + "-";
      }
    },

    formaterTimeStart(val) {
      if (val.length == 2) {
        this.form.quote_start_time = this.form.quote_start_time + ":";
      }
      if (val.length == 5) {
        this.form.quote_start_time = this.form.quote_start_time + ":";
      }
    },

    formaterTimeEnd(val) {
      if (val.length == 2) {
        this.form.quote_end_time = this.form.quote_end_time + ":";
      }
      if (val.length == 5) {
        this.form.quote_end_time = this.form.quote_end_time + ":";
      }
    },

    setPatient(value) {
      const selectedIdentity = String(
        value.id_card_number || value.nik || ""
      );
      const verifiedIdentity = String(
        this.bpjsPatientPrefill.id_card_number || ""
      );
      const preserveBpjsVerification =
        Boolean(this.form.bpjs_verified_at) &&
        selectedIdentity !== "" &&
        selectedIdentity === verifiedIdentity;

      this.form.patient_id = value.id;
      this.display.patient_name = value.name;
      this.bpjsLookupNumber =
        value.id_card_number || value.nik || this.form.bpjs_card_number || "";
      if (!preserveBpjsVerification) this.resetBpjsVerification();
      this.bpjsPatientPrefill = {};
      if (this.currentUser.user_type_id != 1) {
        this.form.doctor_id = "";
      }
      this.doctorDisabled = false;
    },

    resetBpjsVerification() {
      this.form.bpjs_card_number = "";
      this.form.bpjs_participant_status = "";
      this.form.bpjs_verified_at = "";
      this.form.bpjs_fktp_code = "";
      this.form.bpjs_fktp_name = "";
    },

    participantValue(response) {
      let value = response && response.data !== undefined ? response.data : response;
      if (value && value.data !== undefined) value = value.data;
      if (value && value.response !== undefined) value = value.response;
      return value && value.peserta ? value.peserta : value;
    },

    participantField(participant, paths) {
      for (const path of paths) {
        const value = path.split(".").reduce(
          (current, key) => current && current[key],
          participant
        );
        if (value !== undefined && value !== null && value !== "") return value;
      }
      return "";
    },

    normalizeBpjsBirthDate(value) {
      const match = String(value || "").match(/^(\d{2})-(\d{2})-(\d{4})$/);
      return match ? `${match[3]}-${match[2]}-${match[1]}` : String(value || "");
    },

    bpjsParticipantToPatient(participant) {
      const sex = String(
        this.participantField(participant, ["sex", "jenisKelamin"]) || ""
      ).toUpperCase();
      const phone = String(
        this.participantField(participant, [
          "noHP",
          "noHp",
          "mr.noTelepon",
          "mobile_phone",
        ]) || ""
      );

      return {
        name: String(this.participantField(participant, ["nama", "name"]) || ""),
        id_card_number: String(
          this.participantField(participant, ["noKTP", "nik", "no_ktp"]) || ""
        ),
        birt_date: this.normalizeBpjsBirthDate(
          this.participantField(participant, ["tglLahir", "tanggalLahir", "birt_date"])
        ),
        gender: sex === "L" || sex === "LAKI-LAKI" ? "Laki-laki" : "Perempuan",
        title: sex === "L" || sex === "LAKI-LAKI" ? "Tn" : "Nn",
        mobile_phone: phone,
        phone,
        blood_type: String(
          this.participantField(participant, ["golDarah", "golonganDarah"]) || ""
        ),
      };
    },

    async findPatientByNik(nik) {
      if (!nik) return null;
      const response = await ApiService.get(
        "patients",
        `?id_card_number=${encodeURIComponent(nik)}&page_size=1`
      );
      const payload = response && response.data ? response.data : {};
      const patients = Array.isArray(payload.data) ? payload.data : [];
      return patients.length > 0 ? patients[0] : null;
    },

    async verifyBpjsParticipant() {
      if (!this.bpjsLookupValid) return;
      this.bpjsChecking = true;
      try {
        const type = this.bpjsLookupNumber.length === 16 ? "nik" : "nokartu";
        const response = await ApiService.get(
          `bpjs/peserta/${type}/${this.bpjsLookupNumber}`
        );
        if (!response || response.status >= 400) {
          throw new Error(
            (response && response.data && response.data.message) ||
            "Layanan BPJS tidak dapat memverifikasi peserta."
          );
        }
        const participant = this.participantValue(response);
        if (!participant || typeof participant !== "object") {
          throw new Error("Data peserta tidak ditemukan.");
        }
        const status = this.participantField(participant, [
          "statusPeserta.keterangan",
          "statusPeserta",
          "status",
          "aktif",
        ]);
        const normalizedStatus = String(status || "").trim().toUpperCase();
        if (
          normalizedStatus.includes("TIDAK AKTIF") ||
          normalizedStatus.includes("NONAKTIF") ||
          normalizedStatus.includes("NON AKTIF")
        ) {
          throw new Error(
            "Kepesertaan BPJS tidak aktif. Pilih pembayaran Umum atau arahkan peserta memperbarui status JKN."
          );
        }
        this.form.bpjs_card_number = String(
          this.participantField(participant, [
            "noKartu",
            "nomorKartu",
            "no_kartu",
          ]) || this.bpjsLookupNumber
        );
        this.form.bpjs_participant_status = String(status || "Ditemukan");
        this.form.bpjs_verified_at = new Date().toISOString();
        this.form.bpjs_fktp_code = String(
          this.participantField(participant, ["ppk.kdPPK", "fktp.kode"]) || ""
        );
        this.form.bpjs_fktp_name = String(
          this.participantField(participant, ["ppk.nmPPK", "fktp.nama"]) || ""
        );

        const patientData = this.bpjsParticipantToPatient(participant);
        const existingPatient = await this.findPatientByNik(
          patientData.id_card_number
        );

        if (existingPatient) {
          this.form.patient_id = existingPatient.id;
          this.display.patient_name =
            existingPatient.display_name ||
            `${existingPatient.name} (${existingPatient.mobile_phone || "-"})`;
          this.bpjsPatientPrefill = {};
          this.$bvToast.toast(
            "Peserta BPJS sudah terdaftar dan otomatis dipilih sebagai pasien.",
            { title: "Peserta terverifikasi", variant: "success", solid: true }
          );
        } else {
          this.bpjsPatientPrefill = patientData;
          this.$bvModal.show("modal-patient");
          this.$bvToast.toast(
            "Data dari BPJS sudah diisikan. Lengkapi hanya data yang belum tersedia.",
            { title: "Buat pasien baru", variant: "success", solid: true }
          );
        }
      } catch (error) {
        this.resetBpjsVerification();
        const data = error && error.response ? error.response.data : null;
        const message =
          (data && data.metaData && data.metaData.message) ||
          (data && data.message) ||
          error.message ||
          "Data peserta tidak dapat diverifikasi.";
        this.$bvToast.toast(message, {
          title: "Verifikasi BPJS gagal",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.bpjsChecking = false;
      }
    },

    async inputDoctorOnSelect(evt) {
      await evt.id;
      this.selectedDoctorSchedule = await module.get(
        `schedules/get-by-doctor/${evt.id}`
      );
      this.setScheduleText(this.selectedDoctorSchedule);
    },

    handleDoctorOpen() {
      if (this.form.date && this.doctorLoadError) {
        this.getDoctors("retry");
      }
    },

    inputQuoteStartTimeOnHidden() {
      this.getRooms();
    },

    inputQuoteEndTimeOnHidden() {
      this.getRooms();
    },

    setScheduleText(items) {
      if (items.length != 0) {
        let text = `Jadwal Praktek ${items[0].doctor_name} <br>`;
        items.forEach(function (value, index) {
          text += `<b>${value.day_name} ${value.start_time} - ${value.end_time}</b>`;
          if (items.length > 1 && index != items.length - 1) {
            text += ", ";
          }
        });
        this.selectedDoctorScheduleText = text;
      } else {
        this.selectedDoctorScheduleText = "Belum Memiliki Jadwal";
      }
    },

    createDoctorSchedule() {
      setAppointmentForm(this.form);
      this.$router.push(
        `/schedule/list?doctor_id=${this.form.doctor_id}&redirect=appointment-${this.purpose}`
      );
    },

    async getRooms() {
      const requestId = ++this.roomRequestId;
      const selectedRoom = this.form.room_id;
      let timeSlot;
      if (this.form.doctor_time_slot_id != "custom") {
        timeSlot = this.timeSlots.find(
          (x) => x.value == this.form.doctor_time_slot_id
        );
      } else {
        if (
          !this.form.quote_start_time == "" ||
          !this.form.quote_end_time == ""
        ) {
          timeSlot = {
            start_time: this.form.quote_start_time,
            end_time: this.form.quote_end_time,
          };
        }
      }
      var data = [];
      if (timeSlot && timeSlot.start_time && timeSlot.end_time)
        data = await AppointmentModule.getUnusedRoomsTreeSelect(
          this.form.date,
          timeSlot.start_time,
          timeSlot.end_time,
          this.form.id
        );
      else data = await AppointmentModule.getRoomsTreeSelect();
      if (requestId !== this.roomRequestId) return;
      this.rooms = [
        { label: "Pilih Ruangan", id: "", isDisabled: true },
        ...data,
      ];
      if (
        selectedRoom &&
        !data.some((room) => room.id == selectedRoom)
      ) {
        this.form.room_id = "";
      }
    },

    async getActionTypes(doctorId) {
      const requestId = ++this.actionTypeRequestId;
      this.actionTypes = [];
      if (!doctorId) return;

      this.actionTypesLoading = true;
      try {
        const data = await PaymentModule.getPaymentCategoriesTreeSelect(doctorId);
        if (requestId !== this.actionTypeRequestId) return;
        this.actionTypes = Array.isArray(data) ? data : [];
        if (
          this.form.action_type_id &&
          !this.actionTypes.some((action) => action.id == this.form.action_type_id)
        ) {
          this.form.action_type_id = "";
        }
      } catch (error) {
        if (requestId === this.actionTypeRequestId) {
          this.actionTypes = [];
        }
      } finally {
        if (requestId === this.actionTypeRequestId) {
          this.actionTypesLoading = false;
        }
      }
    },

    async getDoctors(source = null) {
      const requestId = ++this.doctorRequestId;
      const selectedDoctor = this.form.doctor_id;
      if (!this.form.date) {
        this.doctors = [];
        if (this.purpose !== "edit") this.form.doctor_id = "";
        return;
      }

      this.doctorsLoading = true;
      this.doctorLoadError = false;
      this.doctors = [];
      try {
        const data = await DoctorModule.getTreeSelect(
          this.form.patient_id,
          this.form.date,
          this.purpose === "edit" ? selectedDoctor : null
        );
        if (requestId !== this.doctorRequestId) return;
        this.doctors = Array.isArray(data) ? data : [];
        if (
          selectedDoctor &&
          !this.doctors.some((doctor) => doctor.id == selectedDoctor)
        ) {
          this.form.doctor_id = "";
          this.form.doctor_time_slot_id = "";
        }
      } catch (error) {
        if (requestId === this.doctorRequestId) {
          this.doctors = [];
          this.doctorLoadError = true;
        }
      } finally {
        if (requestId === this.doctorRequestId) {
          this.doctorsLoading = false;
        }
      }
    },

    async getAppointmentStatuses() {
      const data = await AppointmentModule.getAppointmentStatusesSelect();
      this.statuses.push(...data);
    },

    async getTimeSlots(date, doctorId) {
      this.timeSlots = [];
      var dateObj = new Date(date);
      var weekday = dateObj.toLocaleString("id-ID", { weekday: "long" });
      const data = await AppointmentModule.getTimeSlotsSelect(
        date,
        weekday,
        doctorId,
        this.form.id,
        this.purpose == "edit" && this.form.doctor_time_slot_id == "custom"
      );
      const selectedSlot = data.find(
        (slot) => slot.value == this.form.doctor_time_slot_id
      );
      if (
        this.form.doctor_time_slot_id &&
        (!selectedSlot || selectedSlot.disabled)
      ) {
        this.form.doctor_time_slot_id = "";
      }
      if (data.length === 1) {
        data.push({
          text: "Tidak ada sesi tersedia pada tanggal ini",
          value: null,
          disabled: true,
        });
      }
      this.timeSlots.push(...data);
    },

    async formOnSubmit() {
      if (!this.validateBeforeSubmit()) return;
      let response = await module.submit(this.form, this.route);
      if (response.state == "error") {
        const errorPayload = response.error || {};
        const apiErrors = errorPayload.errors && typeof errorPayload.errors === "object"
          ? errorPayload.errors
          : {};
        const firstApiError = Object.values(apiErrors)
          .reduce((items, value) => items.concat(value || []), [])[0];
        this.formError = firstApiError || errorPayload.message || "Perjanjian belum dapat disimpan.";
        this.error = validation.setValidationError(
          this.error,
          apiErrors,
          this.formError
        );
        if (apiErrors.doctor_time_slot_id && this.form.date && this.form.doctor_id) {
          await this.getTimeSlots(this.form.date, this.form.doctor_id);
        }
        if (apiErrors.room_id) {
          await this.getRooms();
        }
        return;
      }
      removeAppointmentForm();
      this.$root.$emit("UpdateNewAppointment");
      Swal.fire(response.success.title, response.success.message, "success");
      this.$router.push("/appointment/list/0");
    },
  },

  watch: {
    form: {
      deep: true,
      handler() {
        this.formError = "";
      },
    },
    initialForm: {
      deep: true,
      handler(newForm) {
        Object.assign(this.form, newForm || {});
      },
    },
    "form.patient_id": function (newVal) {
      this.getDoctors("wactch");
    },
    "form.coverage_type": function (newVal) {
      if (newVal === "umum") {
        this.resetBpjsVerification();
      }
    },
    "form.patient_name": function (newVal, oldVal) {
      this.display.patient_name = newVal;
    },
    "form.doctor_time_slot_id": function (newVal, oldVal) {
      this.getRooms();
    },
    "form.date": function (newVal, oldVal) {
      if (
        newVal !== oldVal &&
        this.purpose !== "edit" &&
        this.currentUser.user_type_id != 1
      ) {
        this.form.doctor_id = "";
      }
      if (newVal !== oldVal && this.purpose !== "edit") {
        this.form.doctor_time_slot_id = "";
        this.form.action_type_id = "";
        this.actionTypes = [];
        this.timeSlots = [
          { text: "Pilih Jadwal", value: "", disabled: true },
        ];
      }
      this.getDoctors("date");
      if (this.form.doctor_id != "") {
        this.getTimeSlots(newVal, this.form.doctor_id);
      }
    },
    "form.doctor_id": async function (newVal, oldVal) {
      if (typeof newVal === "undefined") this.form.doctor_id = "";
      if (
        newVal !== oldVal &&
        !(this.purpose === "edit" && !oldVal)
      ) {
        this.form.action_type_id = "";
      }
      this.getActionTypes(newVal);
      if (this.form.doctor_id != "") {
        this.selectedDoctorScheduleText = "";
        this.selectedDoctorSchedule = await module.get(
          `schedules/get-by-doctor/${newVal}`
        );
        this.setScheduleText(this.selectedDoctorSchedule);
        this.getTimeSlots(this.form.date, newVal);
      }
    },
  },

  mounted() {
    if (!this.form.coverage_type) this.$set(this.form, "coverage_type", "umum");
    if (this.form.bpjs_card_number) this.bpjsLookupNumber = this.form.bpjs_card_number;
    this.getDoctors("mounted");
    if (this.currentUser.user_type_id == 1)
      this.form.doctor_id = this.currentUser.id;
    this.getAppointmentStatuses();
    this.getRooms();
  },
};
</script>

<style lang="scss" scoped>
/* Styling form agar lebih modern, clean, dan presisi */
.form-group {
  margin-bottom: 0.7rem !important;

  label {
    font-weight: 600 !important;
    font-size: 0.85rem !important;
    color: #475569 !important;
    margin-bottom: 0.4rem !important;
  }
}

.appointment-field-hint {
  display: block;
  margin-top: 0.35rem;
  color: #16867f;
  font-size: 0.72rem;
  font-weight: 400;
}

.appointment-field-hint--warning {
  color: #b7791f;
}

.coverage-panel {
  padding: 14px 16px 8px;
  border: 1px solid #dceae8;
  border-radius: 12px;
  background: #f8fcfb;
}

.coverage-panel__grid {
  display: grid;
  grid-template-columns: minmax(280px, 480px);
  gap: 12px 18px;
  align-items: start;
}

.coverage-panel__grid--bpjs {
  grid-template-columns: minmax(280px, 0.85fr) minmax(420px, 1.65fr);
}

.coverage-panel__complaint {
  margin-top: 2px;
}

.coverage-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding: 8px 2px 4px;

  ::v-deep .custom-control {
    min-width: 130px;
    margin: 0;
    padding-left: 1.7rem;
  }

  ::v-deep .custom-control-label {
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 700 !important;
    color: #334155 !important;
  }

  ::v-deep .custom-control-label::before,
  ::v-deep .custom-control-label::after {
    top: 0.08rem;
    width: 1.15rem;
    height: 1.15rem;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #1E958F;
    background-color: #1E958F;
    box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.12);
  }
}

.bpjs-status {
  min-height: 34px;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #fff7e6;
  color: #925c00;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 2px;
}

.bpjs-status i {
  flex: 0 0 auto;
}

.bpjs-status--ok {
  background: #e9f8f0;
  color: #137a4b;
}

.appointment-patient-picker {
  width: 100%;
  min-height: 42px;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 5px 12px 5px 8px;
  color: #263548;
  background: #FFFFFF;
  border: 1px solid #CBD9E6;
  border-radius: 11px;
  box-shadow: 0 3px 10px rgba(20, 50, 99, 0.04);
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.appointment-patient-picker:focus-visible {
  outline: none;
  border-color: #1E958F;
  box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.13);
}

.appointment-patient-picker__icon {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: grid;
  place-items: center;
  color: #16867F;
  background: #EDF8F6;
  border-radius: 8px;
  font-size: 12px;
}

.appointment-patient-picker__value {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  color: #263548;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.appointment-patient-picker__value--placeholder {
  color: #65778B;
  font-weight: 300;
}

.appointment-patient-picker__arrow {
  flex: 0 0 auto;
  color: #8293A5;
  font-size: 9px;
}

.appointment-patient-picker:disabled {
  color: #7F90A4;
  background: #F2F5F8;
  border-color: #E2E8EF;
  box-shadow: none;
  cursor: not-allowed;
}

.appointment-patient-picker:disabled .appointment-patient-picker__icon {
  color: #8A9AAC;
  background: #E8EDF2;
}

.form-control,
.custom-select {
  border-radius: 8px !important;
  border: 1px solid #E2E8F0 !important;
  font-size: 0.875rem !important;
  height: 38px !important;
  padding: 0.375rem 0.75rem !important;
  color: #334155 !important;
  background-color: #FFFFFF !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;

  &:focus {
    border-color: #1E958F !important;
    box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.1) !important;
    outline: none !important;
  }

  &::placeholder {
    color: #94A3B8 !important;
  }
}

/* Kustomisasi Datepicker & Timepicker prepend button */
::v-deep .input-group-prepend .btn {
  border-radius: 8px 0 0 8px !important;
  border-color: #E2E8F0 !important;
  background-color: #F8FAFC !important;
  color: #64748B !important;
  border-right: none !important;
  padding: 0.375rem 0.75rem !important;
  height: 38px !important;

  &:hover {
    background-color: #F1F5F9 !important;
  }
}

::v-deep .input-group .form-control {
  border-radius: 0 8px 8px 0 !important;
}

/* Treeselect Styling */
::v-deep .vue-treeselect__control {
  border-radius: 8px !important;
  border: 1px solid #E2E8F0 !important;
  height: 38px !important;

  &:focus-within {
    border-color: #1E958F !important;
    box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.1) !important;
  }
}

::v-deep .vue-treeselect__single-value {
  font-size: 0.875rem !important;
  color: #334155 !important;
  line-height: 36px !important;
}

::v-deep .vue-treeselect__placeholder {
  font-size: 0.875rem !important;
  color: #94A3B8 !important;
  line-height: 36px !important;
}

/* Tombol Form Simpan/Batal */
.btn-primary {
  border-radius: 8px !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  padding: 0.5rem 1.5rem !important;
  border: none !important;
  background: linear-gradient(135deg, #1E958F 0%, #14608F 100%) !important;
  color: #FFFFFF !important;
  box-shadow: 0 4px 10px rgba(30, 149, 143, 0.2) !important;
  transition: all 0.2s ease !important;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(30, 149, 143, 0.3) !important;
  }
}

.btn-default {
  border-radius: 8px !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  padding: 0.5rem 1.5rem !important;
  border: 1px solid #CBD5E1 !important;
  background-color: #FFFFFF !important;
  color: #475569 !important;
  transition: all 0.2s ease !important;

  &:hover {
    background-color: #F8FAFC !important;
    color: #1E293B !important;
  }
}

::v-deep .bpjs-lookup .form-control {
  border-radius: 8px 0 0 8px !important;
}

::v-deep .bpjs-lookup .input-group-append .btn {
  min-width: 118px;
  height: 38px;
  padding: 0 14px !important;
  border-radius: 0 8px 8px 0 !important;
  white-space: nowrap;
  font-size: 0.82rem;
  font-weight: 700;
}

::v-deep .appointment-date-trigger {
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 5px 12px 5px 8px;
  border: 1px solid #E2E8F0 !important;
  border-radius: 11px !important;
  background: #FFFFFF !important;
  color: #334155;
  font-size: 13px;
  font-weight: 400;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

::v-deep .appointment-date-trigger:focus-visible {
  outline: none;
  border-color: #1E958F !important;
  box-shadow: 0 0 0 3px rgba(30, 149, 143, 0.1);
}

::v-deep .appointment-date-trigger__icon {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: grid;
  place-items: center;
  color: #16867F;
  background: #EDF8F6;
  border: 1px solid #DCEEEA;
  border-radius: 9px;
  box-shadow: 0 2px 7px rgba(30, 149, 143, 0.07);
  font-size: 12px;
}

::v-deep .appointment-date-trigger__icon i {
  color: inherit !important;
}

::v-deep .appointment-date-trigger .is-placeholder {
  color: #65778B;
  font-weight: 300;
}

::v-deep .appointment-date-trigger__chevron {
  margin-left: auto;
  color: #94A3B8 !important;
  font-size: 11px;
}

::v-deep .appointment-date-trigger:disabled {
  cursor: not-allowed;
  opacity: 0.65;
  background: #F8FAFC !important;
}

::v-deep .appointment-date-calendar {
  width: 100% !important;
  border-radius: 12px !important;
  box-shadow: 0 14px 34px rgba(30, 65, 80, 0.18) !important;
  overflow: hidden;
}

::v-deep .appointment-date-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(30, 65, 80, 0.24);
}

::v-deep .appointment-date-calendar .v-date-picker-header {
  padding: 10px 12px 4px;
}

::v-deep .appointment-date-calendar .v-date-picker-table {
  padding: 0 12px 10px;
}

::v-deep .appointment-date-calendar .v-btn {
  box-shadow: none !important;
  transform: none !important;
}

.appointment-form-error {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 4px 0 14px;
  padding: 11px 13px;
  border: 1px solid #f3c7cb;
  border-radius: 10px;
  background: #fff4f5;
  color: #a83f49;
  font-size: 0.86rem;
  font-weight: 600;
}

.appointment-form-error i {
  color: #d9535f !important;
}

@media (max-width: 991.98px) {
  .coverage-panel__grid,
  .coverage-panel__grid--bpjs {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575.98px) {
  .coverage-panel {
    padding: 12px;
  }

  ::v-deep .bpjs-lookup .input-group-append .btn {
    min-width: 102px;
    padding: 0 10px !important;
  }
}
</style>
